import React, { ComponentProps, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { asView } from '../../../_lib/ViewFetcher';
import { CheckBoxParam, Form, Row, SonForm, SubmitButton, Switch, TextParam, useForm } from '../../../_lib/Form2';
import { Namespace } from '../../../translation/namespace';

type RoleSettingsFormProps = {
  form: SonForm<CheckBoxParam<string> | TextParam<'manualAssignmentPrivilege'> | TextParam<'qualifier'>>;
};

function RoleSettingsDefault({ form }: RoleSettingsFormProps) {
  const { t } = useTranslation(Namespace.RoleSettingsForm);
  const { formProps, params, fetchState } = useForm({
    form,
  });

  // We check for the inheritablePrivilegesParam because at the moment this is the only setting that is not read-only.
  const inheritablePrivilegesParam = params.findByName('inheritablePrivileges');

  const manualAssignmentPrivilegeParam = params.findByName('manualAssignmentPrivilege');
  const qualifierParam = params.findByName('qualifier');

  // Most elements are checkboxes except for two text elements.
  // So instead of getting each individual element via getByName(),
  // and because the order from BE is already correct, we can just check the type of the element.
  const allCheckboxes = params.getAllByType('checkbox');

  const normalizeManualAssignmentPrivilegeValue = (value: string | undefined) => {
    switch (value) {
      case undefined:
        return t('inputs.manualAssignmentPrivilege.values.none');
      case 'none':
      case 'notManual':
        return t(`inputs.manualAssignmentPrivilege.values.${value}`);
      default:
        return value;
    }
  };

  const formId = useId();

  return (
    <Form id={`role-settings-form-${formId}`} modifier="no-padding-bottom" {...formProps}>
      {allCheckboxes?.map(checkbox => (
        <Row key={checkbox.name}>
          <Switch
            label={t(`inputs.${checkbox.name}.label`)}
            param={checkbox}
            modifierLabel="position-inherit"
            disabled={checkbox.name !== 'inheritablePrivileges'}
          />
        </Row>
      ))}
      {(manualAssignmentPrivilegeParam || qualifierParam) && (
        <dl className="role-settings-form__description-list">
          {manualAssignmentPrivilegeParam && (
            <div className="role-settings-form__description-container">
              <dt className="role-settings-form__description-term">
                {`${t(`inputs.manualAssignmentPrivilege.label`)}:`}
              </dt>
              <dd className="role-settings-form__description-detail">
                {normalizeManualAssignmentPrivilegeValue(manualAssignmentPrivilegeParam.value)}
              </dd>
            </div>
          )}
          {qualifierParam && qualifierParam.value?.trim() && (
            <div className="role-settings-form__description-container">
              <dt className="role-settings-form__description-term">{`${t(`inputs.qualifier.label`)}:`}</dt>
              <dd className="role-settings-form__description-detail">{qualifierParam.value}</dd>
            </div>
          )}
        </dl>
      )}
      {inheritablePrivilegesParam && (
        <Row>
          <SubmitButton
            buttonLabel={t('Form:save')}
            messageSuccess={t('Form:status.success')}
            messageSending={t('Form:status.sending')}
            messageFail={t('Form:status.fail')}
            fetchState={fetchState}
          />
        </Row>
      )}
    </Form>
  );
}
export const RoleSettingsDefaultForm = asView(RoleSettingsDefault);
export type RoleSettingsDefaultForm = ComponentProps<typeof RoleSettingsDefault>;
